import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../constants/api";
import { showError, showSuccess } from "../../utils/toast.js";
import { emitEntityAccessDenied, isNotAssignedError } from "../../utils/apiErrorCodes.js";
import { useAuth } from "../ui/AuthContext.jsx";
import useIsMobile from "../ui/useIsMobile.js";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import BlockSectionToolbar from "../ui/BlockSectionToolbar.jsx";
import BlockSectionSearch from "../ui/BlockSectionSearch.jsx";
import UserChip from "../ui/UserChip.jsx";
import AppButton from "../ui/AppButton.jsx";
import { Input, Textarea } from "../forms/shared/FormAtoms.jsx";
import { entityRowDeleteActionClassName, entityRowTonalActionClassName, successButtonLoadingClassName } from "../../design/tokens.js";
function defaultNormalizeComment(row) {
  return {
    id: row?.id,
    user_id: row?.user_id,
    user_name: row?.username || row?.user_name || row?.name || "unbekannt",
    user_avatar: row?.avatar_url || row?.avatar_path || null,
    body: row?.kommentar || row?.text || row?.body || "",
    created_at: row?.created_at || row?.erstellt_am || row?.created || null,
    updated_at: row?.updated_at || row?.geaendert_am || row?.updated || null
  };
}
function defaultRowsParser(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.rows || [];
}
function formatDateTime(iso) {
  try {
    if (!iso) return "";
    return new Date(iso).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
}
export function createEntityCommentsBlock(config) {
  const {
    title = "💬 Kommentare",
    draftKeyPrefix,
    getApiPaths,
    buildCreatePayload = text => ({
      kommentar: text
    }),
    buildUpdatePayload = text => ({
      kommentar: text
    }),
    parseRows = defaultRowsParser,
    normalizeRow = defaultNormalizeComment,
    canEditRow = (row, ctx) => Number(ctx.user?.id) === Number(row?.user_id),
    canDeleteRow = (row, ctx) => Number(ctx.user?.id) === Number(row?.user_id) || ctx.isAdmin,
    emptyMessage = "Keine Kommentare vorhanden.",
    compact = false
  } = config;
  const contentPad = compact ? "px-3 sm:px-4 pb-3 sm:pb-4" : "px-4 sm:px-6 pb-4 sm:pb-5";
  return function EntityCommentsBlock({
    entityId
  }) {
        const {
      user
    } = useAuth();
    const confirm = useConfirm();
    const isMobile = useIsMobile();
    const isAdmin = Number(user?.rolle_id) >= 1 && Number(user?.rolle_id) <= 4;
    const draftKey = useMemo(() => `${draftKeyPrefix}_${entityId || "new"}`, [entityId]);
    const [rows, setRows] = useState([]);
    const [text, setText] = useState("");
    const [editId, setEditId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [suche, setSuche] = useState("");
    const [q, setQ] = useState("");
    const [listLoading, setListLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const apiPaths = useMemo(() => getApiPaths(entityId), [entityId]);
    useEffect(() => {
      const t = setTimeout(() => setQ((suche || "").trim().toLowerCase()), 180);
      return () => clearTimeout(t);
    }, [suche]);
    useEffect(() => {
      const d = sessionStorage.getItem(draftKey);
      if (d && !text) setText(d);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey]);
    useEffect(() => {
      sessionStorage.setItem(draftKey, text || "");
    }, [draftKey, text]);
    const load = useCallback(async () => {
      if (!entityId) return;
      try {
        setListLoading(true);
        const res = await api.get(apiPaths.list, { silent: true });
        const list = parseRows(res.data).map(normalizeRow);
        setRows(list);
      } catch (err) {
        console.error("Kommentare laden fehlgeschlagen:", err);
        setRows([]);
        if (isNotAssignedError(err)) {
          const detail =
            entityId && typeof entityId === "object" && entityId.projektId != null
              ? { entityType: "projekt", entityId: Number(entityId.projektId), projektTyp: entityId.projektTyp }
              : String(apiPaths?.list || "").includes("/service/")
                ? { entityType: "service", entityId: Number(entityId) }
                : null;
          if (detail) emitEntityAccessDenied(detail);
        } else {
          showError("Kommentare konnten nicht geladen werden");
        }
      } finally {
        setListLoading(false);
      }
    }, [entityId, apiPaths, parseRows, normalizeRow]);
    useEffect(() => {
      load();
    }, [load]);
    const handleSubmit = async e => {
      e.preventDefault();
      const body = (text || "").trim();
      if (!body || saving || !entityId) return;
      try {
        setSaving(true);
        if (editId != null) {
          await api.put(apiPaths.update(editId), buildUpdatePayload(body, entityId));
        } else {
          await api.post(apiPaths.create, buildCreatePayload(body, entityId));
        }
        setText("");
        setEditId(null);
        setShowForm(false);
        sessionStorage.removeItem(draftKey);
        await load();
        showSuccess("Kommentar gespeichert");
      } catch (err) {
        console.error("Kommentar speichern fehlgeschlagen:", err);
        showError(err, {
          fallbackKey: "Fehler beim Speichern"
        });
      } finally {
        setSaving(false);
      }
    };
    const handleDelete = async id => {
      const {
        ok
      } = await confirm({
        title: "Kommentar löschen",
        message: "Kommentar wirklich löschen?",
        variant: "danger",
        confirmLabel: "Löschen"
      });
      if (!ok) return;
      try {
        await api.delete(apiPaths.remove(id));
        await load();
        showSuccess("In Papierkorb verschoben");
      } catch (err) {
        console.error("Kommentar löschen fehlgeschlagen:", err);
        showError(err, {
          fallbackKey: "Fehler beim Löschen"
        });
      }
    };
    const filtered = useMemo(() => {
      const sorted = [...rows].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      if (!q) return sorted;
      return sorted.filter(k => {
        const t = (k.body || "").toLowerCase();
        const u = (k.user_name || "").toLowerCase();
        return t.includes(q) || u.includes(q);
      });
    }, [rows, q]);
    const commentCount = rows.length;
    const headerInfo = `${commentCount} Einträge`;
    return (
      <CollapsibleBlock
        compact={compact}
        title={title}
        headerInfo={headerInfo}
        headerInfoMobile={String(commentCount)}
        headerInfoTitle={headerInfo}
        contentClassName={contentPad}
        useChevronUp
        showBorderBottom
        className="overflow-hidden text-blue-50"
      >
        <BlockSectionToolbar
          search={
            <BlockSectionSearch
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              onClear={() => setSuche("")}
              placeholder="Suche…"
              title="Suche nach Kommentartext oder Name …"
              ariaLabel="Kommentare durchsuchen"
              wrapperClassName="relative w-full min-w-0 flex-1 xl:min-w-[20rem]"
            />
          }
          action={
            <AppButton
              type="button"
              onClick={() => {
                if (showForm) {
                  setEditId(null);
                  setText("");
                  try {
                    sessionStorage.removeItem(draftKey);
                  } catch {}
                  setShowForm(false);
                } else {
                  setEditId(null);
                  setShowForm(true);
                }
              }}
              variant="secondary"
              size="sm"
              className="shrink-0"
            >
              {showForm ? "Abbrechen" : "Kommentar hinzufügen"}
            </AppButton>
          }
        />

        {showForm && editId == null ? <form onSubmit={handleSubmit} className="pt-0">
            <div className={"border border-dashed border-white/15 rounded bg-white/5 p-4 space-y-3"}>
              <Textarea rows={3} value={text} onChange={e => setText(e.target.value)} className={"w-full bg-[#0b1324] border border-white/10 rounded px-3 py-2 text-blue-100 text-sm"} placeholder="Kommentar…" />
              <div className="flex justify-end">
                <AppButton type="submit" disabled={saving} variant="success" size="sm" className={saving ? successButtonLoadingClassName : ""}>
                  Speichern
                </AppButton>
              </div>
            </div>
          </form> : null}

        <div className="pt-3 text-sm">
          {listLoading ? <div className="space-y-2 animate-pulse">
              <div className={`h-14 rounded ${"bg-white/10"}`} />
              <div className={`h-14 rounded ${"bg-white/10"}`} />
            </div> : filtered.length === 0 ? <p className={"text-blue-200/70 italic"}>{emptyMessage}</p> : filtered.map(k => {
          const ctx = {
            user,
            isAdmin
          };
          const canEdit = canEditRow(k, ctx);
          const canDelete = canDeleteRow(k, ctx);
          const isEditing = editId === k.id;
          const cancelEdit = () => {
            setEditId(null);
            setText("");
            try {
              sessionStorage.removeItem(draftKey);
            } catch {}
          };
          return <div key={k.id} className={"bg-white/5 border border-white/10 rounded px-4 py-3 mb-2"}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <UserChip user={{
                  id: k.user_id,
                  name: k.user_name,
                  avatar_url: k.user_avatar
                }} variant="table" size="sm" />
                      {!isEditing ? <>
                      <p className="mt-1 whitespace-pre-line break-words">{k.body}</p>
                      <div className={`text-[11px] italic mt-1 ${"text-blue-200/60"}`}>
                        {formatDateTime(k.updated_at || k.created_at)}
                        {k.updated_at ? " · bearbeitet" : ""}
                      </div>
                      </> : null}
                    </div>
                    {!isEditing && (canEdit || canDelete) ? (
                      <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                        {canEdit ? (
                          <AppButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={entityRowTonalActionClassName}
                            onClick={() => {
                              setShowForm(false);
                              setEditId(k.id);
                              setText(k.body || "");
                            }}
                          >
                            Bearbeiten
                          </AppButton>
                        ) : null}
                        {canDelete ? (
                          <AppButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={entityRowDeleteActionClassName}
                            onClick={() => handleDelete(k.id)}
                          >
                            Löschen
                          </AppButton>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="mt-3 border border-dashed border-white/15 rounded bg-white/5 p-3 space-y-3">
                      <Textarea rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="Kommentar…" autoFocus />
                      <div className="flex flex-wrap justify-end gap-2">
                        <AppButton type="button" variant="secondary" size="sm" onClick={cancelEdit}>
                          Abbrechen
                        </AppButton>
                        <AppButton type="submit" disabled={saving} variant="success" size="sm" className={saving ? successButtonLoadingClassName : ""}>
                          {saving ? "Speichern…" : "Änderung speichern"}
                        </AppButton>
                      </div>
                    </form>
                  ) : null}
                </div>;
        })}
        </div>
      </CollapsibleBlock>
    );
  };
}